import * as NextSkyTheme from "@NextSkyTheme/global";
import { eventModal } from "@NextSkyTheme/modal";

/* --------------------------------------------------------------------------
 * TEMPORARY: Kiwi Size Chart → theme size-guide tables
 * Remove this block when native size charts fully replace Kiwi.
 * -------------------------------------------------------------------------- */

const KIWI_UNIT_CM = "1";
const KIWI_UNIT_IN = "0";

function escapeSizeGuideHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Parse Kiwi cell unit map: {"0": inches, "1": cm}
 */
function parseKiwiUnitValues(cell) {
  const text = (cell.textContent || "").trim();
  const raw = cell.getAttribute("data-unit-values");

  if (!raw) {
    return { cm: text, in: text };
  }

  try {
    const values = JSON.parse(raw);
    return {
      cm: values[KIWI_UNIT_CM] ?? text,
      in: values[KIWI_UNIT_IN] ?? text,
    };
  } catch (error) {
    return { cm: text, in: text };
  }
}

/**
 * Extract headers + dual-unit rows from a Kiwi .ks-table root.
 * @returns {{ headers: string[], rows: Array<Array<{cm: string, in: string}>> } | null}
 */
function parseKiwiSizeTable(root) {
  if (!root) return null;

  const table = root.matches?.(".ks-table")
    ? root
    : root.querySelector(".ks-table");

  if (!table) return null;

  const rows = Array.from(table.querySelectorAll(".ks-table-row"));
  if (!rows.length) return null;

  const headers = Array.from(rows[0].querySelectorAll(".ks-table-cell")).map(
    (cell) => (cell.textContent || "").trim()
  );

  if (!headers.length) return null;

  const dataRows = rows.slice(1).map((row) => {
    return Array.from(row.querySelectorAll(".ks-table-cell")).map((cell, index) => {
      // Size label column usually has no unit map
      if (index === 0 || !cell.hasAttribute("data-unit-values")) {
        const text = (cell.textContent || "").trim();
        return { cm: text, in: text };
      }
      return parseKiwiUnitValues(cell);
    });
  });

  return { headers, rows: dataRows };
}

function buildSizeGuideTableMarkup(headers, rows, unit) {
  const head = headers
    .map((header) => `<th scope="col">${escapeSizeGuideHtml(header)}</th>`)
    .join("");

  const body = rows
    .map((row) => {
      const cells = row
        .map((cell) => `<td>${escapeSizeGuideHtml(cell[unit] ?? "")}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<thead><tr>${head}</tr></thead><tbody>${body}</tbody>`;
}

function applyKiwiDataToThemeTables(drawerContent, data) {
  if (!drawerContent || !data?.headers?.length) return false;

  const cmTable = drawerContent.querySelector('[data-size-guide-table-unit="cm"]');
  const inTable = drawerContent.querySelector('[data-size-guide-table-unit="in"]');
  if (!cmTable || !inTable) return false;

  cmTable.innerHTML = buildSizeGuideTableMarkup(data.headers, data.rows, "cm");
  inTable.innerHTML = buildSizeGuideTableMarkup(data.headers, data.rows, "in");
  return true;
}

function findExistingKiwiTableRoot(scope = document) {
  const table =
    scope.querySelector?.(".kiwi-sizing-modal .ks-table") ||
    scope.querySelector?.(".ks-table") ||
    (scope.matches?.(".ks-table") ? scope : null);

  if (!table) return null;

  return (
    table.closest(".ks-chart-individual") ||
    table.closest(".ks-modal-content") ||
    table.closest(".kiwi-sizing-modal") ||
    table
  );
}

function waitForKiwiApi(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (window.ks?.loadSizing && window.KiwiSizing?.data) {
      resolve(true);
      return;
    }

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (window.ks?.loadSizing && window.KiwiSizing?.data) {
        clearInterval(timer);
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 120);
  });
}

function getKiwiProductKey() {
  const data = window.KiwiSizing?.data;
  return String(data?.product ?? data?.productId ?? "");
}

/**
 * Wait until Kiwi paints a .ks-table (in host or elsewhere on the page).
 */
function waitForKiwiTable(host, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const find = () => findExistingKiwiTableRoot(host) || findExistingKiwiTableRoot(document);

    const existing = find();
    if (existing) {
      resolve(existing);
      return;
    }

    let done = false;
    const finish = (node) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(node);
    };

    const observer = new MutationObserver(() => {
      const node = find();
      if (node) finish(node);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Temporary bridge: load/parse Kiwi chart and inject rows into theme CM/IN tables.
 * Keeps theme unit toggle + styles; only table content comes from Kiwi.
 */
async function fillSizeGuideTablesFromKiwi(drawerContent) {
  const log = (...args) => console.info("[SizeGuideKiwi]", ...args);
  const warn = (...args) => console.warn("[SizeGuideKiwi]", ...args);

  if (!drawerContent) {
    warn("No drawer content");
    return false;
  }

  const productKey = getKiwiProductKey();
  if (drawerContent.dataset.kiwiFilled === "true" && drawerContent.dataset.kiwiProduct === productKey) {
    log("Already filled for product", productKey);
    return true;
  }

  // Fast path: Kiwi already rendered a chart/modal on the page
  const existingRoot = findExistingKiwiTableRoot();
  if (existingRoot) {
    const parsed = parseKiwiSizeTable(existingRoot);
    log("Found existing Kiwi table", { parsed });
    if (parsed && applyKiwiDataToThemeTables(drawerContent, parsed)) {
      drawerContent.dataset.kiwiFilled = "true";
      drawerContent.dataset.kiwiProduct = productKey;
      log("Applied from existing Kiwi DOM");
      return true;
    }
  } else {
    log("No existing .ks-table in DOM, trying ks.loadSizing");
  }

  const ready = await waitForKiwiApi();
  log("Kiwi API ready?", ready, {
    hasKs: Boolean(window.ks?.loadSizing),
    hasData: Boolean(window.KiwiSizing?.data),
    kiwiData: window.KiwiSizing?.data || null,
  });
  if (!ready) {
    warn("window.ks.loadSizing or window.KiwiSizing.data missing — open a product page with Kiwi app installed");
    return false;
  }

  // Off-screen but real-size host — Kiwi may skip rendering into 1x1 overflow:hidden nodes
  const host = document.createElement("div");
  host.id = `size-guide-kiwi-temp-${Date.now()}`;
  host.setAttribute("data-size-guide-kiwi-temp", "");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:fixed;left:0;top:0;width:800px;max-height:70vh;overflow:auto;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(host);

  const applyFromRoot = (root, reason) => {
    const parsed = parseKiwiSizeTable(root);
    log("Parse result", { reason, headers: parsed?.headers, rowCount: parsed?.rows?.length });
    if (!parsed) return false;
    if (!applyKiwiDataToThemeTables(drawerContent, parsed)) return false;
    drawerContent.dataset.kiwiFilled = "true";
    drawerContent.dataset.kiwiProduct = getKiwiProductKey();
    log("Done", reason);
    return true;
  };

  try {
    const loadResult = await new Promise((resolve) => {
      let settled = false;
      const finishLoad = (success) => {
        if (settled) return;
        settled = true;
        resolve(success);
      };

      try {
        window.ks.loadSizing({
          productData: window.KiwiSizing.data,
          options: {
            el: host,
            // Also force selector injection into our host (Shopify Kiwi often ignores `el`)
            injectionSelector: `#${host.id}`,
            injectionOrder: 3, // append inside
            displayMode: 0, // inline chart (not link/button modal)
          },
          loaded: (success) => {
            log("ks.loadSizing loaded callback", { success, hostHtmlPreview: host.innerHTML.slice(0, 300) });
            finishLoad(Boolean(success));
          },
        });
      } catch (error) {
        warn("loadSizing threw", error);
        finishLoad(false);
      }

      setTimeout(() => finishLoad(false), 10000);
    });

    if (!loadResult) {
      warn("Failed", "loadSizing returned success=false (no chart matched product)");
      host.remove();
      return false;
    }

    // Table may appear in host OR as a global modal elsewhere
    const tableRoot = await waitForKiwiTable(host, 8000);
    log("Table wait result", {
      found: Boolean(tableRoot),
      hostHtmlLength: host.innerHTML.length,
      tablesInDoc: document.querySelectorAll(".ks-table").length,
      kiwiModals: document.querySelectorAll(".kiwi-sizing-modal").length,
      hostChildren: host.innerHTML.slice(0, 500),
    });

    if (tableRoot && applyFromRoot(tableRoot, "after loadSizing wait")) {
      host.remove();
      return true;
    }

    // Fallback: if Kiwi injected a trigger instead of inline table, open it once
    const trigger =
      host.querySelector("[data-modal-open], .ks-chart-link, .ks-chart-button, a, button") ||
      document.querySelector(".kiwi-sizing-chart-button, .ks-size-chart-link, [class*='kiwi'][class*='button']");

    if (trigger) {
      log("Clicking Kiwi trigger to force modal render", trigger);
      trigger.click();
      const modalRoot = await waitForKiwiTable(document, 5000);
      // Close modal UI if opened
      document.querySelector(".kiwi-sizing-modal [data-modal-close], .ks-modal-close")?.click();
      if (modalRoot && applyFromRoot(modalRoot, "from forced modal")) {
        host.remove();
        return true;
      }
    }

    warn("Failed", "table not found after loadSizing", {
      hostHtmlLength: host.innerHTML.length,
      hostHtml: host.innerHTML.slice(0, 1000),
    });
    host.remove();
    return false;
  } catch (error) {
    warn("Unexpected error", error);
    host.remove();
    return false;
  }
}

/* --------------------------------------------------------------------------
 * /TEMPORARY Kiwi bridge
 * -------------------------------------------------------------------------- */

/**
 * Re-apply .active on tab/segment buttons from panel state.
 * Needed because modal.js removeModalAction strips .active from all buttons on close.
 */
function syncSizeGuideDrawerActiveState(drawerContent) {
  if (!drawerContent) return;

  const mainTabs = drawerContent.querySelectorAll("[data-size-guide-main-tab]");
  const mainPanels = drawerContent.querySelectorAll("[data-size-guide-main-panel]");
  const activeMainPanel = drawerContent.querySelector("[data-size-guide-main-panel].active");
  const mainTarget =
    activeMainPanel?.dataset.sizeGuideMainPanel ||
    mainTabs[0]?.dataset.sizeGuideMainTab;

  if (mainTarget) {
    mainTabs.forEach((tab) => {
      const isActive = tab.dataset.sizeGuideMainTab === mainTarget;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
    });

    mainPanels.forEach((panel) => {
      panel.classList.toggle("active", panel.dataset.sizeGuideMainPanel === mainTarget);
    });
  }

  const categoryButtons = drawerContent.querySelectorAll("[data-size-guide-category]");
  const measurePanels = drawerContent.querySelectorAll("[data-size-guide-measure-panel]");
  const activeMeasurePanel = drawerContent.querySelector(
    "[data-size-guide-measure-panel].active"
  );
  const categoryTarget =
    activeMeasurePanel?.dataset.sizeGuideMeasurePanel ||
    categoryButtons[0]?.dataset.sizeGuideCategory;

  if (categoryTarget) {
    categoryButtons.forEach((button) => {
      button.classList.toggle("active", button.dataset.sizeGuideCategory === categoryTarget);
    });

    measurePanels.forEach((panel) => {
      panel.classList.toggle(
        "active",
        panel.dataset.sizeGuideMeasurePanel === categoryTarget
      );
    });
  }

  const unitToggle = drawerContent.querySelector("[data-size-guide-unit-toggle]");
  const table = drawerContent.querySelector("[data-size-guide-table]");

  if (unitToggle && table) {
    const unit = table.classList.contains("is-inches") ? "in" : "cm";
    unitToggle.querySelectorAll("[data-size-guide-unit]").forEach((button) => {
      button.classList.toggle("active", button.dataset.sizeGuideUnit === unit);
    });
  }
}

function initSizeGuideDrawerInstance(drawerContent) {
  if (!drawerContent || drawerContent.dataset.initialized === "true") return;

  drawerContent.dataset.initialized = "true";

  const mainTabs = drawerContent.querySelectorAll("[data-size-guide-main-tab]");
  const mainPanels = drawerContent.querySelectorAll("[data-size-guide-main-panel]");

  mainTabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const target = tab.dataset.sizeGuideMainTab;

      mainTabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle("active", isActive);
        item.setAttribute("aria-selected", isActive ? "true" : "false");
        item.tabIndex = isActive ? 0 : -1;
      });

      mainPanels.forEach((panel) => {
        panel.classList.toggle("active", panel.dataset.sizeGuideMainPanel === target);
      });
    });
  });

  const categoryButtons = drawerContent.querySelectorAll("[data-size-guide-category]");
  const measurePanels = drawerContent.querySelectorAll("[data-size-guide-measure-panel]");

  categoryButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const target = button.dataset.sizeGuideCategory;

      categoryButtons.forEach((item) => {
        item.classList.toggle("active", item === button);
      });

      measurePanels.forEach((panel) => {
        panel.classList.toggle("active", panel.dataset.sizeGuideMeasurePanel === target);
      });
    });
  });

  const unitToggle = drawerContent.querySelector("[data-size-guide-unit-toggle]");
  const table = drawerContent.querySelector("[data-size-guide-table]");

  if (unitToggle && table) {
    unitToggle.querySelectorAll("[data-size-guide-unit]").forEach((button) => {
      button.addEventListener("click", () => {
        const unit = button.dataset.sizeGuideUnit;

        unitToggle.querySelectorAll("[data-size-guide-unit]").forEach((item) => {
          item.classList.toggle("active", item === button);
        });

        table.classList.toggle("is-inches", unit === "in");
      });
    });
  }

  syncSizeGuideDrawerActiveState(drawerContent);
}

function initSizeGuideDrawer(root = document) {
  const scope = root.querySelectorAll
    ? root
    : document;

  scope.querySelectorAll("[data-size-guide-drawer] .size-guide-drawer").forEach((drawerContent) => {
    initSizeGuideDrawerInstance(drawerContent);
  });
}

function findSizeGuideDrawer(handle) {
  if (!handle) return null;

  const drawers = document.querySelectorAll("[data-size-guide-drawer]");
  for (const drawer of drawers) {
    if (drawer.dataset.sizeGuideHandle === handle) {
      return drawer;
    }
  }

  return null;
}

document.addEventListener("DOMContentLoaded", () => {
  initSizeGuideDrawer();
  // Temporary: show size-chart buttons only when Kiwi app is available
  syncSizeGuideButtonsWithKiwi();
});
document.addEventListener("shopify:section:load", (event) => {
  initSizeGuideDrawer(event.target);
  syncSizeGuideButtonsWithKiwi(event.target);
});

/**
 * Temporary: reveal [data-size-guide-requires-kiwi] buttons only if Kiwi app is on the page.
 * If Kiwi never loads, buttons stay hidden.
 */
async function syncSizeGuideButtonsWithKiwi(root = document) {
  const scope = root.querySelectorAll ? root : document;
  const buttons = scope.querySelectorAll("[data-size-guide-requires-kiwi]");
  if (!buttons.length) return;

  const ready = await waitForKiwiApi(8000);
  buttons.forEach((button) => {
    if (ready) {
      button.hidden = false;
      button.removeAttribute("aria-hidden");
      button.classList.remove("size-guide-kiwi-gated");
    } else {
      button.hidden = true;
      button.setAttribute("aria-hidden", "true");
      console.info("[SizeGuideKiwi] Size chart button hidden — Kiwi app not found");
    }
  });
}

document.addEventListener("click", (event) => {
  const trigger = event.target.closest(".open-size-guide-drawer");
  if (!trigger) return;

  const handle = trigger.dataset.sizeGuideHandle;
  const drawer = findSizeGuideDrawer(handle);
  if (!drawer) return;

  event.preventDefault();

  const drawerContent = drawer.querySelector(".size-guide-drawer");
  initSizeGuideDrawerInstance(drawerContent);
  // Restore tab highlights stripped by modal close cleanup
  syncSizeGuideDrawerActiveState(drawerContent);

  // Temporary: replace theme table rows with Kiwi chart data when enabled
  if (drawer.dataset.sizeGuideUseKiwi === "true") {
    console.info("[SizeGuideKiwi] Drawer open — starting fill");
    fillSizeGuideTablesFromKiwi(drawerContent);
  } else {
    console.info("[SizeGuideKiwi] Skipped — data-size-guide-use-kiwi is not set on drawer. Check Theme settings → Size guide drawer.");
  }

  eventModal(drawer, "open", false);
  NextSkyTheme.global.rootToFocus = trigger;
});
